import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getModals } from 'Selectors';
import { callAll } from 'Utils';
import { clearModals, popModal, pushModal, clearAlerts } from 'Store';

type Args = ModalProp;

const hideAllPresModals = () => {
    const allPresentationModals = document.querySelectorAll('[role="presentation"]');
    allPresentationModals.forEach((m) => {
        (m as HTMLDivElement).style.visibility = 'hidden';
    });
};

const showAllPresModals = () => {
    const allPresentationModals = document.querySelectorAll('[role="presentation"]');
    allPresentationModals.forEach((m) => {
        (m as HTMLDivElement).style.visibility = '';
    });
};

function useReduxModal() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const modals = useSelector(getModals);

    const addModal = React.useCallback(
        ({ id, ...rest }: Args) => {
            dispatch(
                pushModal({
                    id,
                    ...rest
                })
            );
        },
        [dispatch]
    );

    const addTechnicalErrorModal = React.useCallback(
        (preContentMsg?: string, title?: string, onModalClose?: () => void, options: Partial<Args> = {}) => {
            const mainContentMsg = t('Please try again or contact your system administrator for additional assistance');
            const content = preContentMsg ? `${preContentMsg} ${mainContentMsg}` : mainContentMsg;
            hideAllPresModals();

            addModal({
                id: 'technical-error',
                status: 'error',
                title: title || t('Something happened'),
                content,
                cancelText: t('Close'),
                onClose: callAll(showAllPresModals, onModalClose),
                ...options
            });
        },
        [t, addModal]
    );

    const pop = React.useCallback((id: string | number) => dispatch(popModal(id)), [dispatch]);
    const clearAll = React.useCallback(() => {
        dispatch(clearModals());
        dispatch(clearAlerts());
    }, [dispatch]);

    return {
        addModal,
        pop,
        modals,
        clearAll,
        addTechnicalErrorModal
    };
}

export default useReduxModal;
